import { useDocumentsByProject } from '@/features/end-of-lease/api';
import {
    DocType,
    docTypeLabel,
    leasingDocTypeConfig,
} from '@/models/docs';
import { Link } from '@tanstack/react-router';
import { ExternalLink, FileText } from 'lucide-react';

import { cn, formatDate } from '@/lib/utils';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DocTypeBadge } from '@/components/DocTypeBadge';

interface ReferenceDocumentCardProps {
    projectId: number;
    reference: string;
    type: string;
    onOpenDocument: (url: string) => void;
    className?: string;
}

export function ReferenceDocumentCard({
    projectId,
    reference,
    type,
    onOpenDocument,
    className,
}: ReferenceDocumentCardProps) {
    const Icon = leasingDocTypeConfig[type as DocType]?.icon ?? FileText;

    const { data: matchingDocs } = useDocumentsByProject(projectId, reference);
    const docExists = matchingDocs?.results.length !== 0;
    const document = matchingDocs && matchingDocs.results[0];

    return (
        <div
            className={cn(
                'flex items-center justify-between p-3 border rounded-lg transition-all duration-200 hover:shadow-xs bg-white',
                !docExists && 'border-orange-200 bg-orange-50/30',
                className,
            )}
        >
            {/* Left side - Document info */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
                {/* Icon and badge */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <Icon className="h-3 w-3 text-slate-600" />
                    <DocTypeBadge type={type} className="px-1.5 py-0.5" />
                </div>

                {/* Document details */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <h4 className="font-medium text-sm text-slate-900 break-words">
                            {document?.id ? (
                                <Link
                                    to="/lease/projects/$projectId/documents/$documentId/page"
                                    params={{
                                        documentId: document.id.toString(),
                                        projectId: projectId.toString(),
                                    }}
                                >
                                    {document.meta.raw?.title}
                                </Link>
                            ) : (
                                <span className="text-slate-600">
                                    {docTypeLabel(type)} {reference}
                                </span>
                            )}
                        </h4>
                    </div>

                    {document?.meta?.raw?.id && (
                        <p className="text-xs text-slate-500 mb-1">
                            {document.meta.raw.id}
                            {document.meta.revision && (
                                <span className="ml-1">
                                    ({document.meta.revision})
                                </span>
                            )}
                        </p>
                    )}

                    {/* Metadata row */}
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                        {document?.meta?.raw?.issueDate && (
                            <span>
                                Issued:{' '}
                                {formatDate(document.meta.raw.issueDate)}
                            </span>
                        )}
                        {document?.meta?.status && (
                            <Badge
                                variant="outline"
                                className="text-xs h-4 px-1"
                            >
                                {document.meta.status}
                            </Badge>
                        )}
                    </div>
                </div>
            </div>

            {/* Right side - Action button */}
            <div className="flex-shrink-0 ml-3">
                {!docExists ? (
                    <Link
                        to="/documents/upload"
                        search={{
                            projectId: Number(projectId),
                            isProjectView: true,
                        }}
                    >
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs px-2"
                        >
                            Upload file
                        </Button>
                    </Link>
                ) : (
                    document?.presignedUrl && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                onOpenDocument(document?.presignedUrl)
                            }
                            className="h-7 text-xs px-2"
                        >
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Open Document
                        </Button>
                    )
                )}
            </div>
        </div>
    );
}
